import { CompanyActivity, EmploymentStatus, NationalIdType, ProfessionalPosition } from '@nemuru-eng/js-api-client';
import { z } from 'zod';

import {
  CIF_REQUIRED_EMPLOYMENT_STATUS,
  EMPLOYMENT_STATUS_FILTER,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/constants';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import { requiredEnum, requiredFutureDate, requiredPastDate } from '@/utils/zodUtils';
import { validateDocumentNumber } from '@/utils/zodValidation/validateDocumentNumber';

const needsProfessionalDetails = (employmentStatus: EmploymentStatus) =>
  EMPLOYMENT_STATUS_FILTER.find((element) => element === employmentStatus);

const needsCompanyCif = (employmentStatus: EmploymentStatus, isCompanyCifRequired: boolean) =>
  isCompanyCifRequired && CIF_REQUIRED_EMPLOYMENT_STATUS.find((element) => element === employmentStatus);

export const ProfessionalDetailsFormSchema = (
  t: GetTextIdTranslationType,
  additionalProfessionalDetailsRequired: boolean,
  isCompanyCifRequired = false,
) =>
  z
    .object({
      employmentStatus: requiredEnum(EmploymentStatus, t),
      professionalPosition: z.nativeEnum(ProfessionalPosition).optional().nullable(),
      companyName: z.string().optional().nullable(),
      companyActivity: z.nativeEnum(CompanyActivity).optional().nullable(),
      companyCity: z.string().optional().nullable(),
      seniority: requiredPastDate(t).optional().nullable(),
      contractDateEnd: requiredFutureDate(t).optional().nullable(),
      corporateIdNumber: z.string().optional().nullable(),
      selfEmploymentNetIncome: z.string().optional().nullable(),
      selfEmploymentNetIncomeDate: z.date().optional().nullable(),
    })
    .refine((data) => !(data.employmentStatus === EmploymentStatus.TEMPORARY_EMPLOYED && !data.contractDateEnd), {
      message: t('REQUIRED_FIELD'),
      path: ['contractDateEnd'],
    })
    .refine((data) => !(needsProfessionalDetails(data.employmentStatus) && !data.professionalPosition), {
      message: t('REQUIRED_FIELD'),
      path: ['professionalPosition'],
    })
    .refine((data) => !(needsProfessionalDetails(data.employmentStatus) && !data.companyName), {
      message: t('REQUIRED_FIELD'),
      path: ['companyName'],
    })
    .refine((data) => !(needsProfessionalDetails(data.employmentStatus) && !data.companyActivity), {
      message: t('REQUIRED_FIELD'),
      path: ['companyActivity'],
    })
    .refine((data) => !(needsProfessionalDetails(data.employmentStatus) && !data.companyCity), {
      message: t('REQUIRED_FIELD'),
      path: ['companyCity'],
    })
    .refine((data) => !(needsProfessionalDetails(data.employmentStatus) && !data.seniority), {
      message: t('REQUIRED_FIELD'),
      path: ['seniority'],
    })
    .refine((data) => !(needsCompanyCif(data.employmentStatus, isCompanyCifRequired) && !data.corporateIdNumber), {
      message: t('REQUIRED_FIELD'),
      path: ['corporateIdNumber'],
    })
    .refine(
      (data) => (data.corporateIdNumber ? validateDocumentNumber(data.corporateIdNumber, NationalIdType.CIF) : true),
      {
        message: t('INVALID_DOCUMENT_NUMBER'),
        path: ['corporateIdNumber'],
      },
    )
    .refine(
      (data) =>
        !(
          data.employmentStatus === EmploymentStatus.SELF_EMPLOYED &&
          additionalProfessionalDetailsRequired &&
          data.selfEmploymentNetIncomeDate &&
          !data.selfEmploymentNetIncome
        ),
      {
        message: t('REQUIRED_FIELD'),
        path: ['selfEmploymentNetIncome'],
      },
    )
    .refine(
      (data) =>
        !(
          data.employmentStatus === EmploymentStatus.SELF_EMPLOYED &&
          additionalProfessionalDetailsRequired &&
          data.selfEmploymentNetIncome &&
          !data.selfEmploymentNetIncomeDate
        ),
      {
        message: t('REQUIRED_FIELD'),
        path: ['selfEmploymentNetIncomeDate'],
      },
    )
    .refine(
      (data) =>
        !(
          data.employmentStatus === EmploymentStatus.SELF_EMPLOYED &&
          additionalProfessionalDetailsRequired &&
          data.selfEmploymentNetIncomeDate &&
          data.selfEmploymentNetIncomeDate.getFullYear() > new Date().getFullYear()
        ),
      {
        message: t('FUTURE_DATE_NOT_ALLOWED'),
        path: ['selfEmploymentNetIncomeDate'],
      },
    );

export type ProfessionalDetailsFormType = z.infer<ReturnType<typeof ProfessionalDetailsFormSchema>>;
